import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Brand } from "@/components/AppLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, formatError, t } from "@/lib/api";
import { LangToggle } from "@/components/Common";

const DEPTS = ["Général", "Marketing", "Produit", "Opérations", "RH", "Ventes", "Tech"];
const DEPT_EN = { "Général": "General", "Produit": "Product", "Opérations": "Operations", "RH": "HR", "Ventes": "Sales" };

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", department: "Général" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  if (user) return <Navigate to="/app" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(`/auth/${mode}`, body);
      setUser(data);
      toast.success(t(`Bienvenue ${data.name} !`, `Welcome ${data.name}!`));
      navigate("/app");
    } catch (err) {
      setError(formatError(err));
    } finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative z-10 flex flex-col justify-center px-6 sm:px-16 py-12">
        <div className="flex items-center justify-between"><Brand /><LangToggle /></div>
        <h1 className="mt-12 font-display text-4xl font-bold tracking-tight">{mode === "login" ? t("Bon retour !", "Welcome back!") : t("Rejoignez la gym cognitive", "Join the cognitive gym")}</h1>
        <p className="mt-2 text-slate-500">{mode === "login" ? t("Connectez-vous pour reprendre votre entraînement.", "Sign in to resume your training.") : t("Créez votre compte collaborateur en 30 secondes.", "Create your employee account in 30 seconds.")}</p>
        <button data-testid="auth-google-button" onClick={google} type="button" className="mt-8 flex w-full max-w-md items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white py-3 font-semibold hover:border-indigo-300 hover:bg-indigo-50 transition-colors">
          <svg className="h-5 w-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
          {t("Continuer avec Google", "Continue with Google")}
        </button>
        <div className="my-6 flex max-w-md items-center gap-3 text-xs text-slate-400"><div className="h-px flex-1 bg-slate-200" />{t("ou par e-mail", "or with email")}<div className="h-px flex-1 bg-slate-200" /></div>
        <form onSubmit={submit} className="max-w-md space-y-4" data-testid="auth-form">
          {mode === "register" && (
            <>
              <div><Label>{t("Nom complet", "Full name")}</Label><Input data-testid="auth-name-input" value={form.name} onChange={set("name")} required className="mt-1.5 h-11" /></div>
              <div><Label>{t("Service", "Department")}</Label>
                <select data-testid="auth-department-select" value={form.department} onChange={set("department")} className="mt-1.5 h-11 w-full rounded-md border border-input bg-white px-3 text-sm">
                  {DEPTS.map((d) => <option key={d} value={d}>{t(d, DEPT_EN[d] || d)}</option>)}
                </select>
              </div>
            </>
          )}
          <div><Label>{t("E-mail", "Email")}</Label><Input data-testid="auth-email-input" type="email" value={form.email} onChange={set("email")} required className="mt-1.5 h-11" /></div>
          <div><Label>{t("Mot de passe", "Password")}</Label><Input data-testid="auth-password-input" type="password" value={form.password} onChange={set("password")} required minLength={6} className="mt-1.5 h-11" /></div>
          {error && <div data-testid="auth-error" className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">{error}</div>}
          <button data-testid="auth-submit-button" disabled={loading} className="w-full rounded-xl bg-indigo-600 py-3 font-semibold text-white hover:bg-indigo-700 disabled:opacity-60 transition-colors">
            {loading ? t("Patientez…", "Please wait…") : mode === "login" ? t("Se connecter", "Sign in") : t("Créer mon compte", "Create my account")}
          </button>
        </form>
        <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="mt-6 max-w-md text-sm text-slate-500 hover:text-indigo-600 text-left">
          {mode === "login" ? t("Pas encore de compte ? Inscrivez-vous", "No account yet? Sign up") : t("Déjà un compte ? Connectez-vous", "Already have an account? Sign in")}
        </button>
      </div>
      <div className="hidden lg:block relative bg-indigo-600">
        <img src="https://images.pexels.com/photos/39205127/pexels-photo-39205127.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40 mix-blend-luminosity" />
        <div className="absolute bottom-16 left-16 right-16 text-white">
          <div className="font-mono text-xs text-amber-300">{t("DÉFI DU JOUR", "CHALLENGE OF THE DAY")}</div>
          <p className="mt-3 font-display text-3xl font-semibold leading-tight">{t("« Trouvez 3 manières de simplifier votre réunion de 10h. »", "“Find 3 ways to simplify your 10 a.m. meeting.”")}</p>
        </div>
      </div>
    </div>
  );
}
